import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EscenarioResumen, EscenarioData, ResultadoConsolidado } from '../../types/Calculadora';
import calculadoraService from '../../services/calculadoraService';
import { reconciliarEscenario } from '../../data/calculadoraDefaults';
import {
  calcularConsolidado,
  breakEvenDeConsolidado,
  leerBreakEven,
  formatCurrency,
  formatNumber,
  formatPct,
} from './calculadoraHelpers';
import { Card, cn } from '../ui';

interface Props {
  escenarios: EscenarioResumen[];
  /** Escenario que estás editando ahora mismo (refleja cambios sin guardar). */
  currentData: EscenarioData;
  currentName: string;
}

const CLAVE_ACTUAL = 'actual';
const MAX_COLUMNAS = 3;

interface ColumnaState {
  name: string;
  consolidado: ResultadoConsolidado | null;
  loading: boolean;
  error?: string;
}

/** Compara hasta 3 escenarios guardados (más el actual) lado a lado. */
const ComparadorEscenarios: React.FC<Props> = ({ escenarios, currentData, currentName }) => {
  const opciones = useMemo(
    () => [
      { key: CLAVE_ACTUAL, label: currentName },
      ...escenarios.map((e) => ({ key: `id:${e.id}`, label: `${e.name} · ${e.createdBy}` })),
    ],
    [escenarios, currentName],
  );

  const [slots, setSlots] = useState<string[]>(() => {
    const inicial = [CLAVE_ACTUAL];
    if (escenarios[0]) inicial.push(`id:${escenarios[0].id}`);
    return inicial;
  });

  const [cache, setCache] = useState<Record<string, ColumnaState>>({});
  const pedidos = useRef<Set<string>>(new Set());

  // Traer del backend los escenarios guardados que hagan falta (una sola vez).
  useEffect(() => {
    slots.forEach((k) => {
      if (!k.startsWith('id:') || pedidos.current.has(k)) return;
      pedidos.current.add(k);
      const id = Number(k.slice(3));
      setCache((p) => ({ ...p, [k]: { name: 'Cargando…', consolidado: null, loading: true } }));
      calculadoraService
        .obtenerEscenario(id)
        .then((esc) => {
          if (esc?.data) {
            const cons = calcularConsolidado(reconciliarEscenario(esc.data));
            setCache((p) => ({ ...p, [k]: { name: esc.name, consolidado: cons, loading: false } }));
          } else {
            setCache((p) => ({
              ...p,
              [k]: { name: '(no disponible)', consolidado: null, loading: false, error: 'No se pudo leer' },
            }));
          }
        })
        .catch(() => {
          setCache((p) => ({
            ...p,
            [k]: { name: '(error)', consolidado: null, loading: false, error: 'Error al cargar' },
          }));
        });
    });
  }, [slots]);

  const columnaDe = (key: string): ColumnaState => {
    if (key === CLAVE_ACTUAL) {
      return { name: currentName, consolidado: calcularConsolidado(currentData), loading: false };
    }
    return cache[key] ?? { name: 'Cargando…', consolidado: null, loading: true };
  };

  const columnas = slots.map((key) => ({ key, ...columnaDe(key) }));

  const cambiarSlot = (index: number, key: string) => {
    setSlots((prev) => prev.map((k, i) => (i === index ? key : k)));
  };
  const quitarSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };
  const agregarSlot = () => {
    const libre = opciones.find((o) => !slots.includes(o.key));
    if (libre) setSlots((prev) => [...prev, libre.key]);
  };

  const puedeAgregar = slots.length < MAX_COLUMNAS && opciones.length > slots.length;

  // Mejores valores para resaltar (mayor resultado, mayor margen).
  const conDatos = columnas.filter((c) => c.consolidado);
  const mejorResultado = conDatos.length
    ? Math.max(...conDatos.map((c) => c.consolidado!.resultadoAnualProm))
    : null;
  const mejorMargen = conDatos.length
    ? Math.max(...conDatos.map((c) => c.consolidado!.margenPct))
    : null;
  const maxAbsResultado = conDatos.length
    ? Math.max(...conDatos.map((c) => Math.abs(c.consolidado!.resultadoAnualProm)), 1)
    : 1;

  const tono = (positivo: boolean) => (positivo ? 'text-emerald-600' : 'text-red-500');

  const filas: Array<{
    label: string;
    render: (c: ResultadoConsolidado) => React.ReactNode;
    className?: (c: ResultadoConsolidado) => string;
    mejor?: (c: ResultadoConsolidado) => boolean;
  }> = [
    {
      label: 'Suscriptores',
      render: (c) => formatNumber(c.suscriptores),
    },
    {
      label: 'Recaudación anual',
      render: (c) => formatCurrency(c.ingresoAnual),
    },
    {
      label: 'Gasto anual',
      render: (c) => formatCurrency(c.gastoAnualProm),
      className: () => 'text-amber-600',
    },
    {
      label: 'Resultado anual',
      render: (c) => formatCurrency(c.resultadoAnualProm),
      className: (c) => cn('font-semibold', tono(c.resultadoAnualProm >= 0)),
      mejor: (c) => mejorResultado !== null && c.resultadoAnualProm === mejorResultado,
    },
    {
      label: 'Margen',
      render: (c) => (c.ingresoAnual > 0 ? formatPct(c.margenPct) : '—'),
      className: (c) => tono(c.resultadoAnualProm >= 0),
      mejor: (c) => mejorMargen !== null && c.margenPct === mejorMargen && c.ingresoAnual > 0,
    },
    {
      label: 'Punto de equilibrio',
      render: (c) => leerBreakEven(breakEvenDeConsolidado(c.ingresoAnual, c.gastoAnualMin, c.gastoAnualProm, c.gastoAnualMax)).valor,
      className: () => 'text-slate-600',
    },
  ];

  return (
    <Card className="p-6">
      <h2 className="text-base font-semibold text-gappex-secondary mb-1">Comparar escenarios</h2>
      <p className="text-xs text-slate-400 mb-5">
        Elegí hasta {MAX_COLUMNAS} escenarios y mirá los números del mix consolidado lado a lado. El
        actual refleja los cambios sin guardar.
      </p>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm min-w-[620px]">
          <thead>
            <tr>
              <th className="w-44 pb-3" />
              {columnas.map((col, i) => {
                const usadasEnOtros = slots.filter((_, j) => j !== i);
                return (
                  <th key={col.key} className="pb-3 px-3 text-left align-top">
                    <div className="flex items-start gap-1.5">
                      <select
                        value={col.key}
                        onChange={(e) => cambiarSlot(i, e.target.value)}
                        className="flex-1 min-w-0 text-xs font-medium border border-gray-300 rounded-lg px-2 py-1.5 bg-white
                          focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
                      >
                        {opciones
                          .filter((o) => o.key === col.key || !usadasEnOtros.includes(o.key))
                          .map((o) => (
                            <option key={o.key} value={o.key}>
                              {o.label}
                            </option>
                          ))}
                      </select>
                      {slots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => quitarSlot(i)}
                          aria-label="Quitar columna"
                          className="shrink-0 mt-1 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
              {puedeAgregar && (
                <th className="pb-3 px-3 align-top">
                  <button
                    type="button"
                    onClick={agregarSlot}
                    className="text-xs font-medium text-gappex-primary hover:underline whitespace-nowrap mt-1.5"
                  >
                    + Columna
                  </button>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filas.map((fila) => (
              <tr key={fila.label} className="border-t border-slate-100">
                <td className="py-2.5 pr-3 text-xs font-medium text-slate-500">{fila.label}</td>
                {columnas.map((col) => {
                  const c = col.consolidado;
                  return (
                    <td key={col.key} className="py-2.5 px-3 text-right tabular-nums">
                      {col.loading ? (
                        <span className="text-slate-300">…</span>
                      ) : c ? (
                        <span
                          className={cn(
                            'inline-block rounded px-1.5 py-0.5',
                            fila.className?.(c),
                            fila.mejor?.(c) && 'bg-emerald-50 ring-1 ring-emerald-200',
                          )}
                        >
                          {fila.render(c)}
                        </span>
                      ) : (
                        <span className="text-red-400 text-xs">{col.error ?? '—'}</span>
                      )}
                    </td>
                  );
                })}
                {puedeAgregar && <td />}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Comparación visual del resultado anual */}
      {conDatos.length > 1 && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 mb-3">Resultado anual</p>
          <div className="space-y-2.5">
            {columnas.map((col) => {
              const c = col.consolidado;
              if (!c) return null;
              const positivo = c.resultadoAnualProm >= 0;
              const ancho = `${Math.max((Math.abs(c.resultadoAnualProm) / maxAbsResultado) * 100, 1)}%`;
              return (
                <div key={col.key} className="flex items-center gap-3">
                  <span className="w-40 shrink-0 truncate text-xs text-slate-500" title={col.name}>
                    {col.name}
                  </span>
                  <div className="flex-1 h-6 bg-slate-50 rounded-md overflow-hidden">
                    <div
                      className={cn('h-full rounded-md transition-all duration-300', positivo ? 'bg-emerald-500' : 'bg-red-400')}
                      style={{ width: ancho }}
                    />
                  </div>
                  <span className={cn('w-32 shrink-0 text-right text-xs font-semibold tabular-nums', tono(positivo))}>
                    {formatCurrency(c.resultadoAnualProm)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ComparadorEscenarios;
